import { SurveyComponent, Survey } from "@tree-company/tree-query";
import { useMemo } from "react";
import { Navigate, useParams } from "react-router";
import { surveySettingsForStatement } from "../survey";
import { STATEMENT_KEYS } from "../data/statements";
import { LANGUAGE } from "../locales";

/**
 * We create a small wrapper component so that we are sure that the statement exists when we render `StatementSurvey`
 */
export function StatementSurveyWrapper({ apiClient }) {
  const { statement } = useParams();

  // Redirect back to start page if the user went to a url that doesn't exist
  if (!STATEMENT_KEYS.includes(statement)) {
    return <Navigate to={"../"} />;
  }

  return (
    <StatementSurvey
      apiClient={apiClient}
      statement={statement}
      key={statement}
    />
  );
}

export function StatementSurvey({ apiClient, statement }) {
  // If the user has already completed this statement (e.g. they hit the
  // browser back button after picking a new statement on the "next" page),
  // reuse that treesponse instead of starting a fresh survey.
  const completedTreesponse = useMemo(() => {
    return apiClient.treesponses.find(
      (t) => t.getAnswer("statement") === statement && t.completed,
    );
  }, [statement]);

  const treesponse = useMemo(() => {
    if (completedTreesponse) return completedTreesponse;
    return apiClient.initTreesponse({
      language: LANGUAGE,
      surveyVersion: "v2",
      initialAnswers: { statement },
    });
  }, [statement]);

  const survey = useMemo(() => {
    const survey = new Survey(
      surveySettingsForStatement(statement),
      treesponse,
    );
    // The survey config's skipIf (driven by onOptionsLoaded) handles the like
    // page on re-navigation. But on the initial visit the page has already
    // rendered by the time the aggregator responds, so we also chain the
    // callback here to auto-advance when there aren't enough reactions.
    const likeSection = survey.settings.pages
      .find((page) => page.key === "like")
      .sections.find((section) => section.key === "like");
    const originalOnOptionsLoaded = likeSection.onOptionsLoaded;
    likeSection.onOptionsLoaded = (options, question) => {
      originalOnOptionsLoaded?.(options, question);
      if (options.length < 2) {
        survey.nextPage();
      }
    };

    // For an already-completed statement, fast-forward to the final ("next")
    // page so the back button from the following statement lands the user
    // exactly where they chose it from. nextPage() is a no-op at the last
    // page, so the loop terminates safely.
    if (completedTreesponse) {
      let prev = -1;
      while (survey.currentPageIndex !== prev) {
        prev = survey.currentPageIndex;
        survey.nextPage();
      }
    }

    return survey;
  }, [statement]);

  return <SurveyComponent survey={survey} />;
}
